import { MouseEvent, useCallback } from 'react';
import { observer } from 'mobx-react';
import cls from 'classnames';
import { Config } from 'dompurify';

import { SystemAlert, SystemAlertType } from '@/alert/entities/SystemAlert';
import { useAlertStore } from '@/alert/stores/AlertStore';
import Button from '@/common/components/Button/Button';
import Icon from '@/common/components/Icon/Icon';
import RichText from '@/common/components/RichText/RichText';
import { formatMarkdown } from '@/common/helpers/formatMarkdown';
import { useUserStore } from '@/user/stores/UserStore';

import styles from './SystemAlertRow.module.css';

const ICON_CLASSNAME_MAP = {
  [SystemAlertType.Error]: styles.iconError,
  [SystemAlertType.Info]: styles.iconInfo,
  [SystemAlertType.Warning]: styles.iconWarning,
};

const RICH_TEXT_CONFIG: Config = { ADD_ATTR: ['target'] };

interface SystemAlertRowProps {
  alert: SystemAlert;
}

function SystemAlertRow(props: SystemAlertRowProps) {
  const { alert } = props;
  const { closeAlert, closeAlertLocally } = useAlertStore();
  const { isAuthorized } = useUserStore();

  function renderAlertIcon() {
    switch (alert.priority) {
      case SystemAlertType.Error:
        return <Icon name="block" />;
      case SystemAlertType.Warning:
        return <Icon name="warning" />;
      case SystemAlertType.Info:
      default:
        return null;
    }
  }

  const onCloseHandler = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();

      return isAuthorized ? closeAlert(alert.id) : closeAlertLocally(alert.id);
    },
    [alert.id],
  );

  return (
    <div className={styles.alert}>
      <span className={cls(styles.alertIcon, ICON_CLASSNAME_MAP[alert.priority])}>{renderAlertIcon()}</span>

      <RichText aria-label="Alert content" value={formatMarkdown(alert.content)} config={RICH_TEXT_CONFIG} />

      <Button
        className={styles.alertClose}
        size="m"
        aria-label="Close Alert"
        color="transparent"
        onClick={onCloseHandler}
        withIcon
      >
        <Icon name="close" />
      </Button>
    </div>
  );
}

export default observer(SystemAlertRow);
